import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2, Calendar, User, Clock, CheckCircle, XCircle, PhoneCall } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import AuthService from "@/lib/auth";
import { formatDate } from "@/lib/utils";

export default function CallConfirmation() {
  const { code } = useParams<{ code: string }>();
  const [searchParams] = useSearchParams();
  const token = code || searchParams.get("token");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [callDetails, setCallDetails] = useState<any>(null);
  const [responded, setResponded] = useState(false);

  useEffect(() => {
    const fetchDetails = async () => {
      const activeCode = token;
      if (!activeCode) {
        setError("Invalid link. Please check your SMS.");
        setIsLoading(false);
        return;
      }

      try {
        const data = await AuthService.verifyCallAccess(activeCode);
        setCallDetails(data);
      } catch (err: any) {
        console.error("Verification error:", err);
        setError(err.message || "This link has expired or is invalid.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, [token]);

  const handleResponse = async (consent: 'YES' | 'NO') => {
    const activeCode = token;
    if (!activeCode) return;
    
    setIsSubmitting(true);
    try {
      await AuthService.updateCallConsent(activeCode, consent);
      setResponded(true);
      toast.success(consent === 'YES' ? "Call confirmed!" : "Response recorded.");
    } catch (err: any) {
      toast.error("Failed to submit response. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-lg text-gray-600 font-medium">Verifying your invitation...</p>
        </div>
      </div>
    );
  }

  if (error || responded) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
        <Card className="w-full max-w-md shadow-xl border-t-4 border-blue-500">
          <CardHeader className="text-center">
            {responded ? (
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
            ) : (
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <XCircle className="h-10 w-10 text-red-600" />
              </div>
            )}
            <CardTitle className="text-2xl font-bold">
              {responded ? "Thank You!" : "Access Denied"}
            </CardTitle>
            <CardDescription className="text-base mt-2">
              {responded 
                ? "Your response has been recorded. You can now close this window." 
                : error}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center pb-8">
            <Button 
              variant="outline" 
              onClick={() => window.close()}
              className="mt-4"
            >
              Close Window
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-white p-4">
      <Card className="w-full max-w-lg shadow-2xl border-none overflow-hidden">
        <div className="bg-blue-600 p-6 text-white text-center">
          <PhoneCall className="h-12 w-12 mx-auto mb-3 opacity-90" />
          <h1 className="text-2xl font-bold">Call Confirmation</h1>
          <p className="opacity-80">Monitor Health AI Communication Center</p>
        </div>
        
        <CardContent className="p-8">
          <div className="space-y-6">
            <div className="text-center mb-8">
              <p className="text-gray-600 text-lg">
                Hello, you have a follow-up call scheduled regarding your recent health questionnaire.
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-6 space-y-4 border border-gray-100">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                  <User className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Provider</p>
                  <p className="text-lg font-bold text-gray-800">
                    Dr. {callDetails?.doctor_name || "Medical Staff"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                  <Calendar className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Scheduled Date</p>
                  <p className="text-lg font-bold text-gray-800">
                    {formatDate(callDetails?.scheduled_at)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                  <Clock className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Estimated Time</p>
                  <p className="text-lg font-bold text-gray-800">
                    {new Date(callDetails?.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center pt-4">
              <p className="font-semibold text-gray-700 mb-6">
                Are you available to take this call at the scheduled time?
              </p>
              <div className="grid grid-cols-2 gap-4">
                <Button
                  onClick={() => handleResponse('YES')}
                  disabled={isSubmitting}
                  className="bg-green-600 hover:bg-green-700 h-14 text-lg font-bold shadow-lg"
                >
                  {isSubmitting ? <Loader2 className="animate-spin" /> : "YES, I'M OK"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleResponse('NO')}
                  disabled={isSubmitting}
                  className="border-2 border-red-200 text-red-600 hover:bg-red-50 h-14 text-lg font-bold"
                >
                  NO, CHANGE IT
                </Button>
              </div>
            </div>
            
            <p className="text-center text-xs text-gray-400 pt-4">
              This link will expire in 2 hours for your security.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
